import React, { useState, useEffect, useRef } from 'react'
import api from '../../services/api'
import { showSuccess, showError, showWarning, showConfirm } from '../../utils/alert'

function NewMemberTransaction() {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedMember, setSelectedMember] = useState(null)
  const [jumlahSetor, setJumlahSetor] = useState('')
  const [sumberDana, setSumberDana] = useState('kas')
  const [processing, setProcessing] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [transactionData, setTransactionData] = useState(null)
  const [error, setError] = useState(null)
  const [allocation, setAllocation] = useState(null)
  const memberSelectRef = useRef(null)

  useEffect(() => {
    fetchInactiveMembers()
  }, [])

  useEffect(() => {
    calculateAllocation()
  }, [jumlahSetor, sumberDana])

  useEffect(() => {
    if (memberSelectRef.current && members.length > 0) {
      // Initialize Select2
      $(memberSelectRef.current).select2({
        placeholder: '-- Pilih Anggota --',
        allowClear: true,
        width: '100%'
      })

      // Handle change event
      $(memberSelectRef.current).on('change', (e) => {
        const nba = e.target.value
        const member = members.find(m => m.NBA === nba)
        setSelectedMember(member || null)
      })

      // Cleanup
      return () => {
        if (memberSelectRef.current) {
          $(memberSelectRef.current).off('change').select2('destroy')
        }
      }
    }
  }, [members])

  const fetchInactiveMembers = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await api.get('/members', {
        params: { limit: 1000, statusAktif: 0 }
      })
      const data = response.data.data || []
      setMembers(data)
    } catch (error) {
      console.error('Error fetching inactive members:', error)
      setError(error.response?.data?.message || error.message || 'Gagal memuat data anggota')
    } finally {
      setLoading(false)
    }
  }

  const calculateAllocation = async () => {
    if (!jumlahSetor) {
      setAllocation(null)
      return
    }

    try {
      const response = await api.get('/settings/config')
      const data = response.data.data.data
      
      // Handle grouped response structure
      const getConfigValue = (key) => {
        let value = 0
        if (data.simpanan && data.simpanan[key]) {
          value = parseInt(data.simpanan[key].value || '0')
        } else if (data.pendapatan && data.pendapatan[key]) {
          value = parseInt(data.pendapatan[key].value || '0')
        }
        return value
      }

      const simpananPokokMin = getConfigValue('simpanan_pokok_min_registrasi')
      const simpananPokokMax = getConfigValue('simpanan_pokok_default')
      const simpananWajib = getConfigValue('simpanan_wajib_default')
      const biayaAdmin = getConfigValue('pendapatan_admin_anggota_baru')
      const biayaBuku = getConfigValue('pendapatan_uang_buku')
      const biayaSampul = getConfigValue('pendapatan_uang_sampul')
      
      // Get account codes - check all groups
      const getAccountCode = (key) => {
        let value = ''
        const groups = ['umum', 'akun', 'pendapatan', 'simpanan']
        for (const group of groups) {
          if (data[group] && data[group][key]) {
            value = data[group][key].value || ''
            break
          }
        }
        return value
      }
      
      const akunKas = getAccountCode('kode_akun_kas')
      const akunBank = getAccountCode('kode_akun_bank')
      const akunSimpananPokok = getAccountCode('kode_akun_simpanan_pokok')
      const akunSimpananWajib = getAccountCode('kode_akun_simpanan_wajib')
      const akunSimpananSukarela = getAccountCode('kode_akun_simpanan_sukarela')
      const akunPendapatanAdmin = getAccountCode('kode_akun_pendapatan_admin_anggota_baru')
      const akunPendapatanBuku = getAccountCode('kode_akun_pendapatan_buku')
      const akunPendapatanSampul = getAccountCode('kode_akun_pendapatan_sampul')

      // Komponen = sw + min sp + admin + buku + sampul
      const komponen = simpananWajib + simpananPokokMin + biayaAdmin + biayaBuku + biayaSampul
      const setoran = parseInt(jumlahSetor)

      let simpananPokok = 0
      let simpananSukarela = 0
      let meetsMinimum = false
      let sisa = setoran

      // Case: setor < komponen, tolak transaksi
      if (setoran < komponen) {
        setAllocation({
          simpananPokokMin,
          simpananPokokMax,
          simpananWajib,
          biayaAdmin,
          biayaBuku,
          biayaSampul,
          komponen,
          simpananPokok: 0,
          simpananSukarela: 0,
          meetsMinimum: false,
          error: `Setoran kurang dari minimum ${formatCurrency(komponen)}`,
          akunKas,
          akunBank,
          akunSimpananPokok,
          akunSimpananWajib,
          akunSimpananSukarela,
          akunPendapatanAdmin,
          akunPendapatanBuku,
          akunPendapatanSampul
        })
        return
      }

      // Case: setor >= komponen
      // Isi sw dengan nilai dari pengaturan
      sisa -= simpananWajib
      // Isi admin
      sisa -= biayaAdmin
      // Isi buku
      sisa -= biayaBuku
      // Isi sampul
      sisa -= biayaSampul

      // Isi sp dengan sisa nilai komponen tapi tidak boleh melebihi nilai maksimal sp
      simpananPokok = Math.min(sisa, simpananPokokMax)
      sisa -= simpananPokok

      // Cek minimum requirement
      meetsMinimum = simpananPokok >= simpananPokokMin

      // Jika nilai komponen masih lebih maka komponen - sp max
      // Lalu sukarela diisi dengan sisa nilai komponen
      if (sisa > 0) {
        simpananSukarela = sisa
      }

      setAllocation({
        simpananPokokMin,
        simpananPokokMax,
        simpananWajib,
        biayaAdmin,
        biayaBuku,
        biayaSampul,
        komponen,
        simpananPokok,
        simpananSukarela,
        meetsMinimum,
        sisaPokok: simpananPokokMax - simpananPokok,
        akunKas,
        akunBank,
        akunSimpananPokok,
        akunSimpananWajib,
        akunSimpananSukarela,
        akunPendapatanAdmin,
        akunPendapatanBuku,
        akunPendapatanSampul
      })
    } catch (error) {
      console.error('Error calculating allocation:', error)
    }
  }

  const handleProcess = async () => {
    if (!selectedMember || !jumlahSetor) {
      alert('Pilih anggota dan masukkan jumlah setoran')
      return
    }

    if (!sumberDana) {
      alert('Pilih sumber dana')
      return
    }

    if (!allocation || !allocation.meetsMinimum) {
      alert('Simpanan pokok minimum belum terpenuhi. Kekurangan dapat dicicil nanti.')
      return
    }

    setProcessing(true)
    try {
      const response = await api.post(`/members/${selectedMember.NBA}/activate`, {
        jumlahSetor: parseInt(jumlahSetor),
        sumberDana: sumberDana
      })
      setTransactionData(response.data.data)
      setShowConfirmation(true)
      fetchInactiveMembers()
      setJumlahSetor('')
      setSelectedMember(null)
      setSumberDana('kas')
      setAllocation(null)
    } catch (error) {
      alert(error.response?.data?.message || 'Gagal memproses transaksi')
    } finally {
      setProcessing(false)
    }
  }

  const handlePrintReceipt = () => {
    if (!transactionData) return

    // Format for PLQ20/draft printer (80mm width)
    const receiptContent = `
================================
  BUKTI TRANSAKSI
  ANGGOTA BARU
================================
No Bukti : ${transactionData.noBukti || '-'}
NBA      : ${transactionData.nba}
Nama     : ${transactionData.nama || selectedMember?.Nama || '-'}
Tgl      : ${new Date().toLocaleDateString('id-ID')}
Cabang   : ${transactionData.namaCabang || 'Koperasi SP'}
--------------------------------
SETORAN
--------------------------------
Jml Setor: Rp ${parseInt(transactionData.jumlahSetor).toLocaleString('id-ID')}
--------------------------------
ALOKASI
--------------------------------
Simpanan Pokok   : Rp ${transactionData.simpananPokok?.toLocaleString('id-ID') || '0'}
Simpanan Wajib   : Rp ${transactionData.simpananWajib?.toLocaleString('id-ID') || '0'}
Simpanan Sukarela: Rp ${transactionData.simpananSukarela?.toLocaleString('id-ID') || '0'}
Biaya Admin     : Rp ${transactionData.biayaAdmin?.toLocaleString('id-ID') || '0'}
Biaya Buku      : Rp ${transactionData.biayaBuku?.toLocaleString('id-ID') || '0'}
Biaya Sampul    : Rp ${transactionData.biayaSampul?.toLocaleString('id-ID') || '0'}
${transactionData.sisaPokok > 0 ? `Sisa Pokok : Rp ${transactionData.sisaPokok?.toLocaleString('id-ID')} (cicilan)\n` : ''}
================================
Status: AKTIF
================================
  Tanda Tangan
 (${transactionData.namaCabang || 'Koperasi SP'})
================================
`

    const printWindow = window.open('', '_blank')
    printWindow.document.write(`
      <html>
      <head>
        <title>Bukti Transaksi</title>
        <style>
          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            margin: 0;
            padding: 10px;
            width: 80mm;
          }
          pre {
            margin: 0;
            white-space: pre-wrap;
            word-wrap: break-word;
          }
        </style>
      </head>
      <body>
        <pre>${receiptContent}</pre>
      </body>
      </html>
    `)
    printWindow.document.close()
    printWindow.print()
  }

  const formatCurrency = (value) => {
    if (!value) return 'Rp 0'
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value)
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Transaksi Anggota Baru</h3>
        </div>
        <div className="card-body">
          {error && (
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              {error}
            </div>
          )}
          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label>Pilih Anggota (Belum Aktif) *</label>
                <select
                  ref={memberSelectRef}
                  className="form-control select2"
                  style={{ width: '100%' }}
                  disabled={loading}
                >
                  <option value="">-- Pilih Anggota --</option>
                  {members.map((member) => (
                    <option key={member.NBA} value={member.NBA}>
                      {member.NBA} - {member.Nama}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>Sumber Dana *</label>
                <select
                  className="form-control"
                  value={sumberDana}
                  onChange={(e) => setSumberDana(e.target.value)}
                >
                  <option value="kas">Kas</option>
                  <option value="bank">Bank</option>
                </select>
              </div>
              <div className="form-group">
                <label>Jumlah Setoran (Rp) *</label>
                <input
                  type="number"
                  className="form-control"
                  value={jumlahSetor}
                  onChange={(e) => setJumlahSetor(e.target.value)}
                  placeholder="Masukkan jumlah setoran"
                />
              </div>
            </div>
          </div>
          {selectedMember && (
            <div className="alert alert-info mt-3">
              <strong>Anggota Terpilih:</strong><br/>
              NBA: {selectedMember.NBA}<br/>
              Nama: {selectedMember.Nama}<br/>
              Status: {selectedMember.StatusAnggota}
            </div>
          )}

          {allocation && (
            <div className="card card-outline card-info mt-3">
              <div className="card-header">
                <h3 className="card-title">Preview Alokasi Dana</h3>
              </div>
              <div className="card-body">
                {allocation.error ? (
                  <div className="alert alert-danger">
                    <i className="fas fa-times-circle mr-2"></i>
                    <strong>{allocation.error}</strong>
                    <br/>
                    Komponen yang dibutuhkan:
                    <table className="table table-sm mt-2 mb-0">
                      <thead>
                        <tr>
                          <th>Komponen</th>
                          <th>Kode Akun</th>
                          <th className="text-right">Jumlah</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Simpanan Wajib</td>
                          <td>{allocation.akunSimpananWajib || '-'}</td>
                          <td className="text-right">{formatCurrency(allocation.simpananWajib)}</td>
                        </tr>
                        <tr>
                          <td>Simpanan Pokok (min)</td>
                          <td>{allocation.akunSimpananPokok || '-'}</td>
                          <td className="text-right">{formatCurrency(allocation.simpananPokokMin)}</td>
                        </tr>
                        <tr>
                          <td>Biaya Admin</td>
                          <td>{allocation.akunPendapatanAdmin || '-'}</td>
                          <td className="text-right">{formatCurrency(allocation.biayaAdmin)}</td>
                        </tr>
                        <tr>
                          <td>Biaya Buku</td>
                          <td>{allocation.akunPendapatanBuku || '-'}</td>
                          <td className="text-right">{formatCurrency(allocation.biayaBuku)}</td>
                        </tr>
                        <tr>
                          <td>Biaya Sampul</td>
                          <td>{allocation.akunPendapatanSampul || '-'}</td>
                          <td className="text-right">{formatCurrency(allocation.biayaSampul)}</td>
                        </tr>
                        <tr className="table-danger">
                          <td><strong>Total Minimum</strong></td>
                          <td></td>
                          <td className="text-right"><strong>{formatCurrency(allocation.komponen)}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <>
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Komponen</th>
                          <th>Kode Akun</th>
                          <th className="text-right">Jumlah</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Biaya Admin</td>
                          <td>{allocation.akunPendapatanAdmin || '-'}</td>
                          <td className="text-right">{formatCurrency(allocation.biayaAdmin)}</td>
                        </tr>
                        <tr>
                          <td>Biaya Buku</td>
                          <td>{allocation.akunPendapatanBuku || '-'}</td>
                          <td className="text-right">{formatCurrency(allocation.biayaBuku)}</td>
                        </tr>
                        <tr>
                          <td>Biaya Sampul</td>
                          <td>{allocation.akunPendapatanSampul || '-'}</td>
                          <td className="text-right">{formatCurrency(allocation.biayaSampul)}</td>
                        </tr>
                        <tr>
                          <td>Simpanan Wajib</td>
                          <td>{allocation.akunSimpananWajib || '-'}</td>
                          <td className="text-right">{formatCurrency(allocation.simpananWajib)}</td>
                        </tr>
                        <tr className="table-primary">
                          <td><strong>Total Biaya Wajib</strong></td>
                          <td></td>
                          <td className="text-right"><strong>{formatCurrency(allocation.simpananWajib + allocation.biayaAdmin + allocation.biayaBuku + allocation.biayaSampul)}</strong></td>
                        </tr>
                        <tr>
                          <td>Simpanan Pokok</td>
                          <td>{allocation.akunSimpananPokok || '-'}</td>
                          <td className="text-right">{formatCurrency(allocation.simpananPokok)}</td>
                        </tr>
                        {allocation.sisaPokok > 0 && (
                          <tr>
                            <td>Sisa Simpanan Pokok (dapat dicicil)</td>
                            <td>{allocation.akunSimpananPokok || '-'}</td>
                            <td className="text-right">{formatCurrency(allocation.sisaPokok)}</td>
                          </tr>
                        )}
                        {allocation.simpananSukarela > 0 && (
                          <tr className="table-success">
                            <td><strong>Simpanan Sukarela (kelebihan)</strong></td>
                            <td>{allocation.akunSimpananSukarela || '-'}</td>
                            <td className="text-right"><strong>{formatCurrency(allocation.simpananSukarela)}</strong></td>
                          </tr>
                        )}
                        <tr className="table-info">
                          <td><strong>Sumber Dana ({sumberDana === 'kas' ? 'Kas' : 'Bank'})</strong></td>
                          <td>{sumberDana === 'kas' ? allocation.akunKas : allocation.akunBank || '-'}</td>
                          <td className="text-right"><strong>{formatCurrency(parseInt(jumlahSetor))}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                    {!allocation.meetsMinimum && (
                      <div className="alert alert-warning mt-2">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        <strong>Peringatan:</strong> Simpanan pokok minimum adalah {formatCurrency(allocation.simpananPokokMin)}. 
                        Saat ini hanya {formatCurrency(allocation.simpananPokok)}. Kekurangan dapat dicicil pada transaksi berikutnya.
                      </div>
                    )}
                    {allocation.meetsMinimum && (
                      <div className="alert alert-success mt-2">
                        <i className="fas fa-check-circle mr-2"></i>
                        Simpanan pokok minimum terpenuhi. Transaksi dapat diproses.
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          <div className="alert alert-warning mt-3">
            <strong>Aturan Transaksi:</strong><br/>
            - Jumlah setoran akan dialokasikan ke: Biaya Admin, Biaya Buku, Biaya Sampul, Simpanan Wajib, dan Simpanan Pokok<br/>
            - Simpanan Pokok harus memenuhi minimum yang ditentukan<br/>
            - Kekurangan Simpanan Pokok dapat dicicil pada transaksi berikutnya<br/>
            - Kelebihan setoran akan masuk ke Simpanan Sukarela
          </div>

          <button
            className="btn btn-primary mt-3"
            onClick={handleProcess}
            disabled={processing || !selectedMember || !jumlahSetor || !allocation || !allocation.meetsMinimum}
          >
            {processing ? 'Memproses...' : 'Proses Transaksi'}
          </button>
        </div>
      </div>

      {showConfirmation && transactionData && (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Bukti Transaksi</h5>
                <button type="button" className="close" onClick={() => {
                  setShowConfirmation(false)
                  setTransactionData(null)
                }}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="alert alert-success">
                  <i className="fas fa-check-circle mr-2"></i>
                  Transaksi berhasil diproses!
                </div>
                <table className="table table-sm">
                  <tbody>
                    <tr>
                      <td><strong>NBA</strong></td>
                      <td>: {transactionData.nba}</td>
                    </tr>
                    <tr>
                      <td><strong>Jumlah Setor</strong></td>
                      <td>: {formatCurrency(transactionData.jumlahSetor)}</td>
                    </tr>
                    <tr>
                      <td><strong>Simpanan Pokok</strong></td>
                      <td>: {formatCurrency(transactionData.simpananPokok)}</td>
                    </tr>
                    <tr>
                      <td><strong>Simpanan Wajib</strong></td>
                      <td>: {formatCurrency(transactionData.simpananWajib)}</td>
                    </tr>
                    <tr>
                      <td><strong>Simpanan Sukarela</strong></td>
                      <td>: {formatCurrency(transactionData.simpananSukarela)}</td>
                    </tr>
                    <tr>
                      <td><strong>Biaya Admin</strong></td>
                      <td>: {formatCurrency(transactionData.biayaAdmin)}</td>
                    </tr>
                    <tr>
                      <td><strong>Biaya Buku</strong></td>
                      <td>: {formatCurrency(transactionData.biayaBuku)}</td>
                    </tr>
                    <tr>
                      <td><strong>Biaya Sampul</strong></td>
                      <td>: {formatCurrency(transactionData.biayaSampul)}</td>
                    </tr>
                    {transactionData.sisaPokok > 0 && (
                      <tr>
                        <td><strong>Sisa Simpanan Pokok</strong></td>
                        <td>: {formatCurrency(transactionData.sisaPokok)} (dapat dicicil)</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowConfirmation(false)
                  setTransactionData(null)
                }}>
                  Tutup
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handlePrintReceipt}
                >
                  <i className="fas fa-print mr-2"></i>
                  Cetak Bukti
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default NewMemberTransaction
